import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { FiAlertTriangle, FiCheckCircle, FiDownload, FiPackage, FiPrinter, FiRefreshCw, FiSearch, } from 'react-icons/fi';
import api from '../api/axiosConfig';
import { savePrintOrders } from '../api/printOrderService';
import Pagination from '../components/common/Pagination';
import '../components/common/Pagination.css';
const EMPTY_SUMMARY = {
    expectedCount: 0,
    printedCount: 0,
    missingCount: 0,
    printRequestedCount: 0,
    stockMatchedCount: 0,
    stockUnmatchedCount: 0,
    uniqueOrderCount: 0,
};
const EMPTY_COURIER_COUNTS = {
    all: 0,
    cheonil: 0,
    hanjin: 0,
    kyungdong: 0,
    other: 0,
};
const COURIER_FILTER_OPTIONS = [
    { value: 'all', label: '전체', activeClassName: 'btn-dark' },
    { value: 'cheonil', label: '천일', activeClassName: 'btn-primary' },
    { value: 'hanjin', label: '한진', activeClassName: 'btn-success' },
    { value: 'kyungdong', label: '경동', activeClassName: 'btn-warning' },
    { value: 'other', label: '기타', activeClassName: 'btn-secondary' },
];
const FILTER_MODE_LABELS = {
    all: '전체',
    missing: '출력 누락',
    printed: '출력 완료',
    'stock-unmatched': '재고 미매칭',
};
const getTodayString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
const formatDateTime = (value) => {
    if (!value)
        return '-';
    const normalized = String(value).trim().replace(' ', 'T');
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const mi = String(date.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
};
const buildPrintPopupUrl = (orderNumbers, mode) => {
    const normalizedOrderNumbers = (orderNumbers || [])
        .map((value) => String(value || '').trim())
        .filter(Boolean);
    const searchParams = new URLSearchParams();
    const serializedOrderNumbers = normalizedOrderNumbers.join(',');
    const shouldEmbedOrderNumbers = normalizedOrderNumbers.length === 1 || serializedOrderNumbers.length <= 500;
    if (shouldEmbedOrderNumbers && normalizedOrderNumbers.length > 0) {
        searchParams.set('order_numbers', serializedOrderNumbers);
    }
    if (mode) {
        searchParams.set('mode', mode);
    }
    const queryString = searchParams.toString();
    return queryString ? `/print-wbl-popup?${queryString}` : '/print-wbl-popup';
};
const normalizeCourierGroup = (shipComp) => {
    const normalized = String(shipComp || '')
        .trim()
        .replace(/\s+/g, '')
        .toLowerCase();
    if (normalized.includes('천일')) {
        return 'cheonil';
    }
    if (normalized.includes('한진')) {
        return 'hanjin';
    }
    if (normalized.includes('경동')) {
        return 'kyungdong';
    }
    return 'other';
};
const getCourierLabel = (courierFilter) => { var _a; return ((_a = COURIER_FILTER_OPTIONS.find((option) => option.value === courierFilter)) === null || _a === void 0 ? void 0 : _a.label) || '기타'; };
const getCourierBadgeClassName = (courierFilter) => {
    switch (courierFilter) {
        case 'cheonil':
            return 'bg-primary-subtle text-primary';
        case 'hanjin':
            return 'bg-success-subtle text-success';
        case 'kyungdong':
            return 'bg-warning-subtle text-warning-emphasis';
        case 'other':
            return 'bg-secondary-subtle text-secondary';
        default:
            return 'bg-dark-subtle text-dark';
    }
};
const getCompletionRate = (printed, total) => {
    if (!total)
        return 0;
    return Math.round((printed / total) * 100);
};
const getMonitoringInvoiceKey = (row) => {
    const trackingNumber = String(row.trackingNumber || '').trim();
    if (trackingNumber) {
        return `tracking:${trackingNumber}`;
    }
    const shipOrderId = String(row.shipOrderId || '').trim();
    if (shipOrderId && shipOrderId !== '-') {
        return `shipOrder:${shipOrderId}`;
    }
    const orderNumber = String(row.orderNumber || '').trim();
    if (orderNumber && orderNumber !== '-') {
        return `order:${orderNumber}:${row.id}`;
    }
    return `row:${row.id}`;
};
const summarizeMonitoringRows = (targetRows) => {
    const invoiceMap = new Map();
    for (const row of targetRows) {
        const key = getMonitoringInvoiceKey(row);
        const current = invoiceMap.get(key);
        if (!current) {
            invoiceMap.set(key, {
                printCompleted: Boolean(row.printCompleted),
                stockMatched: Boolean(row.stockMatched),
                printRequested: Boolean(row.printRequested),
            });
            continue;
        }
        current.printCompleted = current.printCompleted || Boolean(row.printCompleted);
        current.stockMatched = current.stockMatched || Boolean(row.stockMatched);
        current.printRequested = current.printRequested || Boolean(row.printRequested);
    }
    const invoices = Array.from(invoiceMap.values());
    const total = invoices.length;
    const printed = invoices.filter((invoice) => invoice.printCompleted).length;
    const stockMatched = invoices.filter((invoice) => invoice.stockMatched).length;
    const printRequested = invoices.filter((invoice) => invoice.printRequested).length;
    return {
        total,
        printed,
        missing: total - printed,
        stockMatched,
        stockUnmatched: total - stockMatched,
        printRequested,
    };
};
const applyWorksheetColumnWidths = (worksheet) => {
    worksheet['!cols'] = [
        { wch: 8 },
        { wch: 12 },
        { wch: 14 },
        { wch: 16 },
        { wch: 18 },
        { wch: 12 },
        { wch: 16 },
        { wch: 28 },
        { wch: 10 },
        { wch: 18 },
        { wch: 12 },
        { wch: 14 },
        { wch: 14 },
        { wch: 10 },
        { wch: 12 },
        { wch: 12 },
        { wch: 18 },
        { wch: 16 },
        { wch: 18 },
        { wch: 18 },
    ];
};
const InvoiceMonitoringPage = () => {
    const [selectedDate, setSelectedDate] = useState(getTodayString());
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(EMPTY_SUMMARY);
    const [filterMode, setFilterMode] = useState('all');
    const [courierFilter, setCourierFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [loading, setLoading] = useState(false);
    const [reprinting, setReprinting] = useState(false);
    const [downloadingExcel, setDownloadingExcel] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const fetchMonitoringData = async (targetDate = selectedDate) => {
        var _a, _b, _c, _d;
        setLoading(true);
        setErrorMessage('');
        try {
            const response = await api.get('/api/orders/invoice-monitoring', {
                params: { date: targetDate || undefined },
            });
            const nextRows = Array.isArray((_a = response.data) === null || _a === void 0 ? void 0 : _a.rows) ? response.data.rows : [];
            setRows(nextRows);
            setSummary(((_b = response.data) === null || _b === void 0 ? void 0 : _b.summary) || EMPTY_SUMMARY);
            setSelectedIds([]);
        }
        catch (error) {
            setRows([]);
            setSummary(EMPTY_SUMMARY);
            setSelectedIds([]);
            setErrorMessage(((_d = (_c = error === null || error === void 0 ? void 0 : error.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.message) || (error === null || error === void 0 ? void 0 : error.message) || '송장 모니터링 데이터를 불러오지 못했습니다.');
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchMonitoringData(selectedDate);
    }, [selectedDate]);
    const rowsByOrderNumber = useMemo(() => {
        var _a;
        const map = new Map();
        for (const row of rows) {
            const key = String(row.orderNumber || '').trim();
            if (!key)
                continue;
            if (!map.has(key)) {
                map.set(key, []);
            }
            (_a = map.get(key)) === null || _a === void 0 ? void 0 : _a.push(row);
        }
        return map;
    }, [rows]);
    const baseFilteredRows = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        return rows.filter((row) => {
            if (filterMode === 'missing' && row.printCompleted) {
                return false;
            }
            if (filterMode === 'printed' && !row.printCompleted) {
                return false;
            }
            if (filterMode === 'stock-unmatched' && row.stockMatched) {
                return false;
            }
            if (!normalizedSearch) {
                return true;
            }
            const target = [
                row.orderNumber,
                row.shipOrderId,
                row.trackingNumber,
                row.recipientName,
                row.productCode,
                row.productName,
                row.shipComp,
            ]
                .join(' ')
                .toLowerCase();
            return target.includes(normalizedSearch);
        });
    }, [filterMode, rows, searchTerm]);
    const courierCounts = useMemo(() => {
        const groupedKeys = {
            all: new Set(),
            cheonil: new Set(),
            hanjin: new Set(),
            kyungdong: new Set(),
            other: new Set(),
        };
        for (const row of baseFilteredRows) {
            const invoiceKey = getMonitoringInvoiceKey(row);
            groupedKeys.all.add(invoiceKey);
            groupedKeys[normalizeCourierGroup(row.shipComp)].add(invoiceKey);
        }
        return {
            all: groupedKeys.all.size,
            cheonil: groupedKeys.cheonil.size,
            hanjin: groupedKeys.hanjin.size,
            kyungdong: groupedKeys.kyungdong.size,
            other: groupedKeys.other.size,
        };
    }, [baseFilteredRows]);
    const filteredRows = useMemo(() => {
        if (courierFilter === 'all') {
            return baseFilteredRows;
        }
        return baseFilteredRows.filter((row) => normalizeCourierGroup(row.shipComp) === courierFilter);
    }, [baseFilteredRows, courierFilter]);
    const courierSheetRows = useMemo(() => ({
        all: baseFilteredRows,
        cheonil: baseFilteredRows.filter((row) => normalizeCourierGroup(row.shipComp) === 'cheonil'),
        hanjin: baseFilteredRows.filter((row) => normalizeCourierGroup(row.shipComp) === 'hanjin'),
        kyungdong: baseFilteredRows.filter((row) => normalizeCourierGroup(row.shipComp) === 'kyungdong'),
        other: baseFilteredRows.filter((row) => normalizeCourierGroup(row.shipComp) === 'other'),
    }), [baseFilteredRows]);
    const currentCourierOption = COURIER_FILTER_OPTIONS.find((option) => option.value === courierFilter) ||
        COURIER_FILTER_OPTIONS[0];
    const selectedCourierSummary = useMemo(() => summarizeMonitoringRows(filteredRows), [filteredRows]);
    const courierStatusCards = useMemo(() => COURIER_FILTER_OPTIONS.filter((option) => option.value !== 'all').map((option) => {
        const optionRows = baseFilteredRows.filter((row) => normalizeCourierGroup(row.shipComp) === option.value);
        const optionSummary = summarizeMonitoringRows(optionRows);
        return {
            ...option,
            total: optionSummary.total,
            printed: optionSummary.printed,
            missing: optionSummary.missing,
            completionRate: getCompletionRate(optionSummary.printed, optionSummary.total),
        };
    }), [baseFilteredRows]);
    const totalPages = itemsPerPage === -1 ? 1 : Math.max(1, Math.ceil(filteredRows.length / itemsPerPage));
    const currentRows = itemsPerPage === -1
        ? filteredRows
        : filteredRows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    useEffect(() => {
        setCurrentPage(1);
    }, [courierFilter, filterMode, searchTerm, selectedDate]);
    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);
    const selectableRows = useMemo(() => filteredRows.filter((row) => !row.printCompleted), [filteredRows]);
    useEffect(() => {
        const visibleSelectableIds = new Set(selectableRows.map((row) => row.id));
        setSelectedIds((prev) => prev.filter((id) => visibleSelectableIds.has(id)));
    }, [selectableRows]);
    const handleToggleRow = (rowId, checked) => {
        if (checked) {
            setSelectedIds((prev) => (prev.includes(rowId) ? prev : [...prev, rowId]));
            return;
        }
        setSelectedIds((prev) => prev.filter((value) => value !== rowId));
    };
    const handleToggleAll = (checked) => {
        if (checked) {
            setSelectedIds(selectableRows.map((row) => row.id));
            return;
        }
        setSelectedIds([]);
    };
    const buildExcelRows = (targetRows) => targetRows.map((row, index) => ({
        No: index + 1,
        택배사구분: getCourierLabel(normalizeCourierGroup(row.shipComp)),
        택배사원본: row.shipComp || '-',
        주문번호: row.orderNumber || '-',
        출고SEQ: row.shipOrderId || '-',
        수취인: row.recipientName || '-',
        상품코드: row.productCode || '-',
        상품명: row.productName || '-',
        수량: Number(row.quantity || 0),
        송장번호: row.trackingNumber || '-',
        출력상태: row.printCompleted ? '출력완료' : '출력누락',
        재고차감매칭: row.stockMatched ? '매칭' : '미매칭',
        로케이션: row.location || '-',
        현재고: Number(row.currentStock || 0),
        출력요청여부: row.printRequested ? 'Y' : 'N',
        출력요청횟수: Number(row.printLogCount || 0),
        최근출력요청일시: formatDateTime(row.lastPrintRequestedAt),
        이슈: row.issueType || '-',
        생성일시: formatDateTime(row.createdAt),
        수정일시: formatDateTime(row.updatedAt),
    }));
    const handleDownloadExcel = () => {
        if (baseFilteredRows.length === 0) {
            window.alert('다운로드할 송장 데이터가 없습니다.');
            return;
        }
        setDownloadingExcel(true);
        try {
            const workbook = XLSX.utils.book_new();
            const summarySheetRows = [
                {
                    다운로드일시: formatDateTime(new Date().toISOString()),
                    기준일자: selectedDate,
                    상태필터: FILTER_MODE_LABELS[filterMode],
                    택배사필터: currentCourierOption.label,
                    검색어: searchTerm.trim() || '-',
                    현재조회행수: filteredRows.length,
                    전체: courierCounts.all,
                    천일: courierCounts.cheonil,
                    한진: courierCounts.hanjin,
                    경동: courierCounts.kyungdong,
                    기타: courierCounts.other,
                    출력완료: selectedCourierSummary.printed,
                    출력누락: selectedCourierSummary.missing,
                    재고미매칭: selectedCourierSummary.stockUnmatched,
                    출력요청존재: selectedCourierSummary.printRequested,
                    출력완료율: `${getCompletionRate(selectedCourierSummary.printed, selectedCourierSummary.total)}%`,
                },
            ];
            const summaryWorksheet = XLSX.utils.json_to_sheet(summarySheetRows);
            summaryWorksheet['!cols'] = [
                { wch: 18 },
                { wch: 12 },
                { wch: 14 },
                { wch: 12 },
                { wch: 12 },
                { wch: 10 },
                { wch: 8 },
                { wch: 8 },
                { wch: 8 },
                { wch: 8 },
                { wch: 8 },
                { wch: 10 },
                { wch: 10 },
                { wch: 12 },
                { wch: 12 },
                { wch: 12 },
            ];
            XLSX.utils.book_append_sheet(workbook, summaryWorksheet, '요약');
            const sheetConfigs = [
                { name: '현재조회', rows: filteredRows },
                { name: '전체', rows: courierSheetRows.all },
                { name: '천일', rows: courierSheetRows.cheonil },
                { name: '한진', rows: courierSheetRows.hanjin },
                { name: '경동', rows: courierSheetRows.kyungdong },
                { name: '기타', rows: courierSheetRows.other },
            ];
            for (const sheetConfig of sheetConfigs) {
                const exportRows = buildExcelRows(sheetConfig.rows);
                const worksheet = XLSX.utils.json_to_sheet(exportRows.length > 0 ? exportRows : [{ 안내: '데이터 없음' }]);
                if (exportRows.length > 0) {
                    applyWorksheetColumnWidths(worksheet);
                }
                else {
                    worksheet['!cols'] = [{ wch: 20 }];
                }
                XLSX.utils.book_append_sheet(workbook, worksheet, sheetConfig.name);
            }
            const fileName = `송장모니터링_${selectedDate}_${currentCourierOption.label}_${FILTER_MODE_LABELS[filterMode]}.xlsx`;
            XLSX.writeFile(workbook, fileName);
        }
        finally {
            setDownloadingExcel(false);
        }
    };
    const handleOpenReprint = async () => {
        var _a, _b;
        const selectedRows = rows.filter((row) => selectedIds.includes(row.id) && !row.printCompleted);
        if (selectedRows.length === 0) {
            window.alert('재출력할 누락 송장을 먼저 선택해주세요.');
            return;
        }
        const orderNumbers = Array.from(new Set(selectedRows
            .map((row) => String(row.orderNumber || '').trim())
            .filter(Boolean)));
        const partiallyPrintedOrders = orderNumbers.filter((orderNumber) => {
            const orderRows = rowsByOrderNumber.get(orderNumber) || [];
            const printedCount = orderRows.filter((row) => row.printCompleted).length;
            const missingCount = orderRows.filter((row) => !row.printCompleted).length;
            return printedCount > 0 && missingCount > 0;
        });
        if (partiallyPrintedOrders.length > 0) {
            const shouldContinue = window.confirm(`선택한 주문 중 ${partiallyPrintedOrders.length}건은 일부 송장만 누락된 상태입니다.\n기존 재발행 팝업은 주문번호 기준으로 열리므로 이미 출력된 송장도 함께 재발행될 수 있습니다.\n계속 진행하시겠습니까?`);
            if (!shouldContinue) {
                return;
            }
        }
        setReprinting(true);
        try {
            await savePrintOrders(orderNumbers);
            const printWindow = window.open(buildPrintPopupUrl(orderNumbers, 'reissue'), '_blank', 'width=1200,height=800');
            if (!printWindow) {
                window.alert('팝업이 차단되었습니다. 브라우저 팝업 허용 후 다시 시도해주세요.');
            }
        }
        catch (error) {
            window.alert(((_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || (error === null || error === void 0 ? void 0 : error.message) || '재출력 준비 중 오류가 발생했습니다.');
        }
        finally {
            setReprinting(false);
        }
    };
    return (_jsxs("div", { className: "container-fluid py-3", children: [_jsxs("div", { className: "d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4", children: [_jsxs("div", { children: [_jsx("h4", { className: "page-title mb-1", children: "\uC1A1\uC7A5 \uBAA8\uB2C8\uD130\uB9C1 \uBC0F \uC7AC\uCD9C\uB825" }), _jsx("nav", { "aria-label": "breadcrumb", children: _jsxs("ol", { className: "breadcrumb mb-0", children: [_jsx("li", { className: "breadcrumb-item", children: "\uCD9C\uACE0\uAD00\uB9AC" }), _jsx("li", { className: "breadcrumb-item active", children: "\uC1A1\uC7A5 \uBAA8\uB2C8\uD130\uB9C1" })] }) })] }), _jsxs("div", { className: "d-flex flex-wrap gap-2", children: [_jsxs("button", { type: "button", className: "btn btn-outline-secondary", onClick: () => fetchMonitoringData(selectedDate), disabled: loading, children: [_jsx(FiRefreshCw, { className: "me-1" }), "\uC0C8\uB85C\uACE0\uCE68"] }), _jsxs("button", { type: "button", className: "btn btn-outline-success", onClick: handleDownloadExcel, disabled: loading || downloadingExcel || baseFilteredRows.length === 0, children: [_jsx(FiDownload, { className: "me-1" }), "\uC5D1\uC140 \uB2E4\uC6B4\uB85C\uB4DC"] }), _jsxs("button", { type: "button", className: "btn btn-danger", onClick: handleOpenReprint, disabled: reprinting || selectedIds.length === 0, children: [_jsx(FiPrinter, { className: "me-1" }), "\uC120\uD0DD \uB204\uB77D\uAC74 \uC7AC\uCD9C\uB825"] })] })] }), _jsxs("div", { className: "row g-3 mb-4", children: [_jsx("div", { className: "col-md-3", children: _jsx("div", { className: "card border-0 shadow-sm h-100", children: _jsxs("div", { className: "card-body", children: [_jsx("div", { className: "text-muted small mb-2", children: "\uC608\uC0C1 \uC1A1\uC7A5 \uC218\uB7C9" }), _jsx("div", { className: "fs-3 fw-bold text-dark", children: summary.expectedCount.toLocaleString('ko-KR') }), _jsx("div", { className: "small text-muted mt-2", children: "\uC624\uB298 \uC0DD\uC131\uB41C \uC1A1\uC7A5\uD589 \uAE30\uC900" })] }) }) }), _jsx("div", { className: "col-md-3", children: _jsx("div", { className: "card border-0 shadow-sm h-100", children: _jsxs("div", { className: "card-body", children: [_jsx("div", { className: "text-muted small mb-2", children: "\uC2E4\uC81C \uCD9C\uB825 \uC644\uB8CC" }), _jsx("div", { className: "fs-3 fw-bold text-success", children: summary.printedCount.toLocaleString('ko-KR') }), _jsx("div", { className: "small text-muted mt-2", children: "tracking_no \uC874\uC7AC \uD589 \uC218" })] }) }) }), _jsx("div", { className: "col-md-3", children: _jsx("div", { className: "card border-0 shadow-sm h-100", children: _jsxs("div", { className: "card-body", children: [_jsx("div", { className: "text-muted small mb-2", children: "\uCD9C\uB825 \uB204\uB77D" }), _jsx("div", { className: "fs-3 fw-bold text-danger", children: summary.missingCount.toLocaleString('ko-KR') }), _jsx("div", { className: "small text-muted mt-2", children: "\uC989\uC2DC \uC7AC\uCD9C\uB825 \uB300\uC0C1" })] }) }) }), _jsx("div", { className: "col-md-3", children: _jsx("div", { className: "card border-0 shadow-sm h-100", children: _jsxs("div", { className: "card-body", children: [_jsx("div", { className: "text-muted small mb-2", children: "\uC7AC\uACE0 \uCC28\uAC10 \uB9E4\uCE6D" }), _jsx("div", { className: "fs-3 fw-bold text-primary", children: summary.stockMatchedCount.toLocaleString('ko-KR') }), _jsxs("div", { className: "small text-muted mt-2", children: ["\uBBF8\uB9E4\uCE6D ", summary.stockUnmatchedCount.toLocaleString('ko-KR'), "\uAC74"] })] }) }) })] }), _jsx("div", { className: "card border-0 shadow-sm mb-4", children: _jsx("div", { className: "card-body", children: _jsxs("div", { className: "row g-3 align-items-end", children: [_jsxs("div", { className: "col-md-2", children: [_jsx("label", { className: "form-label", children: "\uC870\uD68C\uC77C\uC790" }), _jsx("input", { type: "date", className: "form-control", value: selectedDate, onChange: (event) => setSelectedDate(event.target.value) })] }), _jsxs("div", { className: "col-md-2", children: [_jsx("label", { className: "form-label", children: "\uC0C1\uD0DC \uD544\uD130" }), _jsxs("select", { className: "form-select", value: filterMode, onChange: (event) => setFilterMode(event.target.value), children: [_jsx("option", { value: "all", children: "\uC804\uCCB4" }), _jsx("option", { value: "missing", children: "\uCD9C\uB825 \uB204\uB77D" }), _jsx("option", { value: "printed", children: "\uCD9C\uB825 \uC644\uB8CC" }), _jsx("option", { value: "stock-unmatched", children: "\uC7AC\uACE0 \uBBF8\uB9E4\uCE6D" })] })] }), _jsxs("div", { className: "col-md-6", children: [_jsx("label", { className: "form-label", children: "\uD1B5\uD569 \uAC80\uC0C9" }), _jsxs("div", { className: "input-group", children: [_jsx("span", { className: "input-group-text", children: _jsx(FiSearch, {}) }), _jsx("input", { type: "text", className: "form-control", placeholder: "\uC8FC\uBB38\uBC88\uD638, \uC1A1\uC7A5\uBC88\uD638, \uC218\uCDE8\uC778, \uC0C1\uD488\uCF54\uB4DC, \uC0C1\uD488\uBA85, \uD0DD\uBC30\uC0AC", value: searchTerm, onChange: (event) => setSearchTerm(event.target.value) })] })] }), _jsxs("div", { className: "col-md-2", children: [_jsx("div", { className: "small text-muted mb-2", children: "\uC624\uB298 \uC8FC\uBB38 \uC218" }), _jsxs("div", { className: "fw-semibold", children: [summary.uniqueOrderCount.toLocaleString('ko-KR'), "\uAC74"] })] })] }) }) }), _jsx("div", { className: "card border-0 shadow-sm", children: _jsxs("div", { className: "card-body", children: [errorMessage ? _jsx("div", { className: "alert alert-danger mb-3", children: errorMessage }) : null, _jsxs("div", { className: "d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3", children: [_jsx("div", { className: "d-flex flex-wrap gap-2", children: COURIER_FILTER_OPTIONS.map((option) => (_jsxs("button", { type: "button", className: `btn btn-sm ${courierFilter === option.value ? option.activeClassName : 'btn-outline-secondary'}`, onClick: () => setCourierFilter(option.value), children: [option.label, _jsx("span", { className: "ms-2 badge text-bg-light", children: courierCounts[option.value].toLocaleString('ko-KR') })] }, option.value))) }), _jsxs("div", { className: "small text-muted", children: ["\uD604\uC7AC \uD0DD\uBC30\uC0AC: ", _jsx("strong", { children: currentCourierOption.label }), " \u00B7 \uCD1D", ' ', selectedCourierSummary.total.toLocaleString('ko-KR'), "\uD589 \u00B7 \uCD9C\uB825 \uB204\uB77D", ' ', selectedCourierSummary.missing.toLocaleString('ko-KR'), "\uAC74"] })] }), _jsxs("div", { className: "row g-2 mb-3", children: [_jsx("div", { className: "col-12 col-xl-4", children: _jsxs("div", { className: "border rounded-3 bg-light h-100 px-3 py-3", children: [_jsx("div", { className: "small text-muted mb-2", children: "\uD604\uC7AC \uC870\uD68C \uC694\uC57D" }), _jsxs("div", { className: "d-flex flex-wrap gap-3", children: [_jsxs("div", { children: [_jsx("div", { className: "text-muted small", children: "\uD544\uD130" }), _jsxs("div", { className: "fw-semibold", children: [currentCourierOption.label, " / ", FILTER_MODE_LABELS[filterMode]] })] }), _jsxs("div", { children: [_jsx("div", { className: "text-muted small", children: "\uC644\uB8CC\uC728" }), _jsxs("div", { className: "fw-bold text-success", children: [getCompletionRate(selectedCourierSummary.printed, selectedCourierSummary.total), "%"] })] }), _jsxs("div", { children: [_jsx("div", { className: "text-muted small", children: "\uCD9C\uB825\uC694\uCCAD \uC874\uC7AC" }), _jsxs("div", { className: "fw-semibold", children: [selectedCourierSummary.printRequested.toLocaleString('ko-KR'), "\uAC74"] })] })] })] }) }), courierStatusCards.map((card) => (_jsx("div", { className: "col-6 col-xl-2", children: _jsxs("div", { className: `border rounded-3 h-100 px-3 py-3 ${courierFilter === card.value ? 'border-dark shadow-sm' : 'border-light-subtle'}`, children: [_jsxs("div", { className: "d-flex align-items-center justify-content-between mb-2", children: [_jsx("span", { className: `badge ${getCourierBadgeClassName(card.value)}`, children: card.label }), _jsxs("span", { className: "small text-muted", children: [card.completionRate, "%"] })] }), _jsx("div", { className: "fs-5 fw-bold", children: card.total.toLocaleString('ko-KR') }), _jsxs("div", { className: "small text-muted mt-1", children: ["\uB204\uB77D ", card.missing.toLocaleString('ko-KR'), "\uAC74"] })] }) }, card.value)))] }), _jsxs("div", { className: "d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3", children: [_jsxs("div", { className: "small text-muted", children: ["\uCD1D ", filteredRows.length.toLocaleString('ko-KR'), "\uD589", selectedIds.length > 0
                                            ? `, 선택 ${selectedIds.length.toLocaleString('ko-KR')}건`
                                            : ''] }), _jsx("div", { className: "small text-muted", children: "\uC1A1\uC7A5 \uB2E8\uC704 \uD45C\uC2DC: \uC8FC\uBB38 1\uAC74\uC5D0 \uC1A1\uC7A5 2\uAC1C, \uC0C1\uD488 2\uAC1C\uBA74 4\uD589\uC73C\uB85C \uD45C\uC2DC\uB429\uB2C8\uB2E4." })] }), _jsx("div", { className: "table-responsive", children: _jsxs("table", { className: "table table-bordered table-hover align-middle", style: { minWidth: '1500px' }, children: [_jsx("thead", { className: "table-light", children: _jsxs("tr", { children: [_jsx("th", { className: "text-center", style: { width: '40px' }, children: _jsx("input", { type: "checkbox", className: "form-check-input", checked: selectableRows.length > 0 && selectedIds.length === selectableRows.length, onChange: (event) => handleToggleAll(event.target.checked) }) }), _jsx("th", { className: "text-center", children: "No" }), _jsx("th", { className: "text-center", children: "\uC8FC\uBB38\uBC88\uD638" }), _jsx("th", { className: "text-center", children: "\uCD9C\uACE0SEQ" }), _jsx("th", { className: "text-center", children: "\uD0DD\uBC30\uC0AC" }), _jsx("th", { className: "text-center", children: "\uC218\uCDE8\uC778" }), _jsx("th", { className: "text-center", children: "\uC0C1\uD488\uCF54\uB4DC" }), _jsx("th", { className: "text-center", children: "\uC0C1\uD488\uBA85" }), _jsx("th", { className: "text-center", children: "\uC218\uB7C9" }), _jsx("th", { className: "text-center", children: "\uC1A1\uC7A5\uBC88\uD638" }), _jsx("th", { className: "text-center", children: "\uCD9C\uB825\uC0C1\uD0DC" }), _jsx("th", { className: "text-center", children: "\uC7AC\uACE0\uCC28\uAC10\uB9E4\uCE6D" }), _jsx("th", { className: "text-center", children: "\uB85C\uCF00\uC774\uC158" }), _jsx("th", { className: "text-center", children: "\uD604\uC7AC\uACE0" }), _jsx("th", { className: "text-center", children: "\uCD9C\uB825\uC694\uCCAD" }), _jsx("th", { className: "text-center", children: "\uC774\uC288" })] }) }), _jsx("tbody", { children: loading ? (_jsx("tr", { children: _jsx("td", { colSpan: 16, className: "text-center py-5 text-muted", children: "\uC1A1\uC7A5 \uBAA8\uB2C8\uD130\uB9C1 \uB370\uC774\uD130\uB97C \uBD88\uB7EC\uC624\uB294 \uC911\uC785\uB2C8\uB2E4." }) })) : currentRows.length === 0 ? (_jsx("tr", { children: _jsx("td", { colSpan: 16, className: "text-center py-5 text-muted", children: "\uC870\uD68C\uB41C \uC1A1\uC7A5\uD589\uC774 \uC5C6\uC2B5\uB2C8\uB2E4." }) })) : (currentRows.map((row, index) => (_jsxs("tr", { className: !row.printCompleted ? 'table-warning' : undefined, children: [_jsx("td", { className: "text-center", children: _jsx("input", { type: "checkbox", className: "form-check-input", disabled: row.printCompleted, checked: selectedIds.includes(row.id), onChange: (event) => handleToggleRow(row.id, event.target.checked) }) }), _jsx("td", { className: "text-center", children: itemsPerPage === -1
                                                        ? index + 1
                                                        : (currentPage - 1) * itemsPerPage + index + 1 }), _jsx("td", { className: "text-center", children: row.orderNumber || '-' }), _jsx("td", { className: "text-center", children: row.shipOrderId || '-' }), _jsx("td", { className: "text-center", children: _jsx("span", { className: `badge ${getCourierBadgeClassName(normalizeCourierGroup(row.shipComp))}`, children: row.shipComp || '-' }) }), _jsx("td", { children: row.recipientName || '-' }), _jsx("td", { children: row.productCode || '-' }), _jsx("td", { children: row.productName || '-' }), _jsx("td", { className: "text-center", children: Number(row.quantity || 0).toLocaleString('ko-KR') }), _jsx("td", { className: "text-center", children: row.trackingNumber || '-' }), _jsx("td", { className: "text-center", children: _jsx("span", { className: `badge ${row.printCompleted ? 'bg-success' : 'bg-danger'}`, children: row.printCompleted ? '출력완료' : '출력누락' }) }), _jsx("td", { className: "text-center", children: _jsx("span", { className: `badge ${row.stockMatched ? 'bg-primary' : 'bg-warning text-dark'}`, children: row.stockMatched ? '매칭' : '미매칭' }) }), _jsx("td", { className: "text-center", children: row.location || '-' }), _jsx("td", { className: "text-end", children: Number(row.currentStock || 0).toLocaleString('ko-KR') }), _jsx("td", { className: "text-center", children: row.printRequested ? (_jsxs("div", { children: [_jsxs("div", { className: "small fw-semibold", children: [row.printLogCount, "\uD68C"] }), _jsx("div", { className: "text-muted", style: { fontSize: '11px' }, children: formatDateTime(row.lastPrintRequestedAt) })] })) : ('-') }), _jsx("td", { children: _jsxs("div", { className: "d-flex flex-column gap-1", children: [_jsx("span", { className: "small fw-semibold", children: row.issueType || '-' }), _jsxs("span", { className: "text-muted", style: { fontSize: '11px' }, children: ["\uC0DD\uC131 ", formatDateTime(row.createdAt)] })] }) })] }, row.id)))) })] }) }), _jsx("div", { className: "mt-3", children: _jsx(Pagination, { currentPage: currentPage, totalPages: totalPages, itemsPerPage: itemsPerPage, totalItems: filteredRows.length, selectedItems: selectedIds, onPageChange: setCurrentPage, onItemsPerPageChange: setItemsPerPage }) })] }) }), _jsxs("div", { className: "row g-3 mt-1", children: [_jsx("div", { className: "col-md-4", children: _jsx("div", { className: "card border-0 shadow-sm h-100", children: _jsxs("div", { className: "card-body", children: [_jsxs("div", { className: "d-flex align-items-center gap-2 mb-2", children: [_jsx(FiPackage, { className: "text-primary" }), _jsx("strong", { children: "\uC608\uC0C1 \uAE30\uC900" })] }), _jsxs("div", { className: "small text-muted", children: ["\uC624\uB298 \uC0DD\uC131\uB41C ", _jsx("code", { children: "tb_orders_seq" }), " \uD589\uC744 \uC1A1\uC7A5 \uB2E8\uC704 \uC608\uC815 \uBAA9\uB85D\uC73C\uB85C \uC0AC\uC6A9\uD569\uB2C8\uB2E4."] })] }) }) }), _jsx("div", { className: "col-md-4", children: _jsx("div", { className: "card border-0 shadow-sm h-100", children: _jsxs("div", { className: "card-body", children: [_jsxs("div", { className: "d-flex align-items-center gap-2 mb-2", children: [_jsx(FiCheckCircle, { className: "text-success" }), _jsx("strong", { children: "\uCD9C\uB825 \uC644\uB8CC \uAE30\uC900" })] }), _jsxs("div", { className: "small text-muted", children: [_jsx("code", { children: "tracking_no" }), " \uAC00 \uC874\uC7AC\uD558\uBA74 \uC2E4\uC81C \uCD9C\uB825 \uC644\uB8CC\uB85C \uC9D1\uACC4\uD569\uB2C8\uB2E4."] })] }) }) }), _jsx("div", { className: "col-md-4", children: _jsx("div", { className: "card border-0 shadow-sm h-100", children: _jsxs("div", { className: "card-body", children: [_jsxs("div", { className: "d-flex align-items-center gap-2 mb-2", children: [_jsx(FiAlertTriangle, { className: "text-warning" }), _jsx("strong", { children: "\uC7AC\uACE0 \uCC28\uAC10 \uB9E4\uCE6D \uAE30\uC900" })] }), _jsxs("div", { className: "small text-muted", children: ["\uB85C\uCF00\uC774\uC158 \uCC28\uAC10 \uC774\uB825(", _jsx("code", { children: "tb_location_detail_history" }), " \uC758 ", _jsx("code", { children: "OUTBOUND" }), ") \uB610\uB294 \uC720\uD6A8 \uB85C\uCF00\uC774\uC158\uC774 \uC788\uC73C\uBA74 \uB9E4\uCE6D\uC73C\uB85C \uD45C\uC2DC\uD569\uB2C8\uB2E4."] })] }) }) })] })] }));
};
export default InvoiceMonitoringPage;
